import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Camera, ScanLine, X } from 'lucide-react';
import {
  getQrmarkScanner,
  getQrmarkScannerSupport,
  releaseQrmarkScanner,
} from '../services/qrmarkScanner';
import type { QrmarkScanResult, QrmarkScanner } from '../services/qrmarkScanner';
import {
  Code128DiagnosticResult,
  Code128DiagnosticSession,
  preloadDynamsoftCode128,
  startDynamsoftCode128Diagnostic,
  startDynamsoftQrDiagnostic,
} from '../services/dynamsoftCode128Diagnostic';
import {
  DELIVERY_TASK_RE,
  extractDeliveryBusinessCode,
  FORMAL_TRACE_RE,
  PMIG_RE,
} from '../utils/pdaCodes';
import { setPdaInfraredScannerEnabled } from '../utils/pdaScannerControl';

export interface QrmarkCameraPayload {
  error: string | null;
  found: boolean;
  ms: number;
  ok: boolean;
  qrText: string;
  result: string | null;
  serial: string;
  source: string;
}

interface QrmarkCameraScannerProps {
  disabled?: boolean;
  embedded?: boolean;
  mode?: 'pmi' | 'plain';
  onOpenChange?: (open: boolean) => void;
  onScan: (payload: QrmarkCameraPayload) => void | Promise<void>;
  plainCodeKind?: 'any' | 'formal' | 'task';
  preloadEngines?: boolean;
}

function getErrorMessage(error: unknown) {
  if (error instanceof DOMException) {
    if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
      return '摄像头权限被拒绝，请在 PDA 浏览器设置中允许访问摄像头';
    }
    if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
      return '没有找到可用摄像头';
    }
    if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
      return '摄像头正在被其他程序占用';
    }
  }
  return error instanceof Error ? error.message : String(error);
}

function isFatalQrmarkError(message: string) {
  return /识别引擎已释放|识别 Worker|WebAssembly|算法文件加载失败|识别引擎加载超时/.test(message);
}

function toPayload(result: QrmarkScanResult, detectedCode: string): QrmarkCameraPayload {
  return {
    error: result.error || null,
    found: true,
    ms: result.ms,
    ok: true,
    qrText: result.qrText,
    result: detectedCode,
    serial: result.serial,
    source: result.source,
  };
}

function stopStream(stream: MediaStream | null) {
  stream?.getTracks().forEach((track) => track.stop());
}

type Code128EngineState = 'failed' | 'idle' | 'loading' | 'ready';

const CAMERA_SCAN_INTERVAL_MS = 120;

function getPlainCameraCode(rawText: string, kind: 'any' | 'formal' | 'task') {
  const code = extractDeliveryBusinessCode(rawText, kind === 'formal' ? 'formal' : 'tagged');
  if (kind === 'formal') {
    return FORMAL_TRACE_RE.test(code) ? code : '';
  }
  if (kind === 'task') {
    return DELIVERY_TASK_RE.test(code) ? code : '';
  }
  return code;
}

function getScanningPrompt(
  code128State: Code128EngineState,
  code128Attempts = 0,
  code128Error = '',
) {
  if (code128State === 'ready') {
    if (code128Attempts > 0) {
      return `条码已分析 ${code128Attempts} 帧 · 请对准 80×80 或 60×60 标签`;
    }
    return '请将 80×80 隐写码或 60×60 条形码完整放入取景框';
  }
  if (code128State === 'failed') {
    return code128Error
      ? `60×60 条形码不可用：${code128Error}；80×80 识别仍可用`
      : '60×60 条形码引擎不可用；80×80 识别仍可用';
  }
  return '隐写码识别已就绪，60×60 条形码引擎加载中…';
}

function QrmarkCameraScanner({
  disabled = false,
  embedded = false,
  mode = 'pmi',
  onOpenChange,
  onScan,
  plainCodeKind = 'any',
  preloadEngines = false,
}: QrmarkCameraScannerProps) {
  const cameraContainerRef = useRef<HTMLDivElement>(null);
  const fallbackVideoRef = useRef<HTMLVideoElement>(null);
  const activeVideoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const scannerRef = useRef<QrmarkScanner | null>(null);
  const code128SessionRef = useRef<Code128DiagnosticSession | null>(null);
  const cameraStopPromiseRef = useRef<Promise<void>>(Promise.resolve());
  const closingPromiseRef = useRef<Promise<void> | null>(null);
  const mountedRef = useRef(true);
  const animationFrameRef = useRef(0);
  const scanTimerRef = useRef(0);
  const sessionRef = useRef(0);
  const openRef = useRef(false);
  const busyRef = useRef(false);
  const code128StateRef = useRef<Code128EngineState>('idle');
  const code128AttemptsRef = useRef(0);
  const code128ErrorRef = useRef('');
  const lastNonPmigCodeRef = useRef('');
  const scanFrameRef = useRef<(maxSide?: number, continueScanning?: boolean) => Promise<void>>(
    async () => undefined,
  );
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [cameraMode, setCameraMode] = useState<'dynamsoft' | 'fallback' | 'loading'>('loading');
  const [status, setStatus] = useState('正在启动摄像头…');
  const [statusTone, setStatusTone] = useState<'error' | 'info' | 'success'>('info');

  const cancelScheduledScan = useCallback(() => {
    window.clearTimeout(scanTimerRef.current);
    scanTimerRef.current = 0;
    window.cancelAnimationFrame(animationFrameRef.current);
    animationFrameRef.current = 0;
  }, []);

  const scheduleScan = useCallback((delay = CAMERA_SCAN_INTERVAL_MS) => {
    cancelScheduledScan();
    if (!openRef.current) {
      return;
    }
    scanTimerRef.current = window.setTimeout(() => {
      if (!openRef.current) {
        return;
      }
      animationFrameRef.current = window.requestAnimationFrame(() => {
        void scanFrameRef.current(1440, true);
      });
    }, delay);
  }, [cancelScheduledScan]);

  const stopFallbackCamera = useCallback(() => {
    const fallbackVideo = fallbackVideoRef.current;
    if (!fallbackVideo) {
      return;
    }
    fallbackVideo.pause();
    fallbackVideo.srcObject = null;
  }, []);

  const closeCamera = useCallback(async () => {
    if (closingPromiseRef.current) {
      await closingPromiseRef.current;
      return;
    }

    sessionRef.current += 1;
    openRef.current = false;
    cancelScheduledScan();
    setClosing(true);
    setStatus('正在释放摄像头并恢复红外扫描…');
    setStatusTone('info');
    const code128Session = code128SessionRef.current;
    code128SessionRef.current = null;
    if (code128Session) {
      cameraStopPromiseRef.current = cameraStopPromiseRef.current
        .catch(() => undefined)
        .then(() => code128Session.stop())
        .catch(() => undefined);
    }
    code128StateRef.current = 'idle';
    code128AttemptsRef.current = 0;
    code128ErrorRef.current = '';
    activeVideoRef.current = null;
    stopStream(streamRef.current);
    streamRef.current = null;
    stopFallbackCamera();
    busyRef.current = false;
    lastNonPmigCodeRef.current = '';

    const closingPromise = cameraStopPromiseRef.current
      .catch(() => undefined)
      .then(() => setPdaInfraredScannerEnabled(true))
      .then(() => undefined);
    closingPromiseRef.current = closingPromise;
    await closingPromise;
    closingPromiseRef.current = null;

    if (!mountedRef.current) {
      return;
    }
    setCameraMode('loading');
    setClosing(false);
    setOpen(false);
    onOpenChange?.(false);
  }, [cancelScheduledScan, onOpenChange, stopFallbackCamera]);

  const scanFrame = useCallback(async (maxSide = 1440, continueScanning = true) => {
    const video = activeVideoRef.current;
    const scanner = scannerRef.current;
    if (!openRef.current || busyRef.current) {
      return;
    }
    if (!scanner) {
      return;
    }
    if (!video || video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
      if (continueScanning) {
        scheduleScan();
      }
      return;
    }

    busyRef.current = true;
    try {
      const result = await scanner.scan(video, maxSide);
      if (!openRef.current) {
        return;
      }
      if (result.found && result.serial) {
        setStatus(`已识别追溯码 ${result.serial} · ${result.ms}ms`);
        setStatusTone('success');
        const payload = toPayload(result, result.serial);
        await closeCamera();
        await onScan(payload);
        return;
      }
      const qrText = result.qrText.trim();
      if (mode === 'pmi' && PMIG_RE.test(qrText)) {
        setStatus(`已识别 PMIG ${qrText} · ${result.ms}ms`);
        setStatusTone('success');
        const payload = toPayload(result, qrText.toUpperCase());
        await closeCamera();
        await onScan(payload);
        return;
      }
      if (mode === 'plain' && qrText) {
        const businessCode = getPlainCameraCode(qrText, plainCodeKind);
        if (businessCode) {
          setStatus(`已识别扫码内容 ${businessCode} · ${result.ms}ms`);
          setStatusTone('success');
          const payload = toPayload(result, businessCode);
          await closeCamera();
          await onScan(payload);
          return;
        }
      }
      if (result.error && result.error !== '未定位到二维码') {
        throw new Error(result.error);
      }
      if (result.qrText) {
        scanner.setHint(result.qrText);
        setStatus('已定位二维码，正在提取隐写追溯码…');
      } else if (maxSide === 0) {
        setStatus('高清识别未命中，请调整距离或角度');
      } else {
        setStatus(getScanningPrompt(
          code128StateRef.current,
          code128AttemptsRef.current,
          code128ErrorRef.current,
        ));
      }
      setStatusTone('info');
    } catch (error) {
      if (!openRef.current) {
        return;
      }
      const errorMessage = getErrorMessage(error);
      const suffix = mode === 'pmi' && code128StateRef.current === 'ready'
        ? '；60×60 条形码识别继续运行'
        : '';
      setStatus(`${mode === 'plain' ? '扫码识别' : '隐写码识别'}异常：${errorMessage}${suffix}`);
      setStatusTone('error');
      // PDA 切换摄像头、首帧到达以及低端 CPU 解码时都可能只失败一帧。
      // 单帧异常不代表引擎损坏，继续下一帧；只有 Worker/WASM 已失效才释放。
      if (isFatalQrmarkError(errorMessage)) {
        scannerRef.current = null;
        releaseQrmarkScanner();
      }
    } finally {
      busyRef.current = false;
      if (continueScanning && openRef.current && scannerRef.current) {
        scheduleScan();
      }
    }
  }, [closeCamera, mode, onScan, plainCodeKind, scheduleScan]);

  scanFrameRef.current = scanFrame;

  const submitCode128Result = useCallback(async (result: Code128DiagnosticResult) => {
    if (!openRef.current) {
      return;
    }
    const code = result.text.trim();
    if (!code) {
      return;
    }
    if (!PMIG_RE.test(code)) {
      if (code !== lastNonPmigCodeRef.current) {
        lastNonPmigCodeRef.current = code;
        setStatus(`已读取非 PMIG 条形码：${code}`);
        setStatusTone('info');
      }
      return;
    }

    const normalizedCode = code.toUpperCase();
    setStatus(`已识别 PMIG ${normalizedCode}`);
    setStatusTone('success');
    const payload: QrmarkCameraPayload = {
      error: null,
      found: true,
      ms: 0,
      ok: true,
      qrText: normalizedCode,
      result: normalizedCode,
      serial: '',
      source: result.format || 'CODE_128',
    };
    await closeCamera();
    await onScan(payload);
  }, [closeCamera, onScan]);

  const submitPlainQrResult = useCallback(async (result: Code128DiagnosticResult) => {
    if (!openRef.current) {
      return;
    }
    const qrText = result.text.trim();
    const businessCode = getPlainCameraCode(qrText, plainCodeKind);
    if (!businessCode) {
      return;
    }

    setStatus(`已识别扫码内容 ${businessCode}`);
    setStatusTone('success');
    const payload: QrmarkCameraPayload = {
      error: null,
      found: true,
      ms: 0,
      ok: true,
      qrText,
      result: businessCode,
      serial: '',
      source: result.format || 'QR_CODE',
    };
    await closeCamera();
    await onScan(payload);
  }, [closeCamera, onScan, plainCodeKind]);

  const openCamera = useCallback(async () => {
    if (disabled || openRef.current || closingPromiseRef.current) {
      return;
    }
    const support = getQrmarkScannerSupport();
    openRef.current = true;
    setOpen(true);
    onOpenChange?.(true);
    setStatus(support.supported
      ? mode === 'plain' ? '正在启动摄像头扫码…' : '正在加载识别引擎和摄像头…'
      : support.reason);
    setStatusTone(support.supported ? 'info' : 'error');
    if (!support.supported) {
      return;
    }

    const session = sessionRef.current += 1;
    code128StateRef.current = mode === 'pmi' ? 'loading' : 'idle';
    code128AttemptsRef.current = 0;
    code128ErrorRef.current = '';
    lastNonPmigCodeRef.current = '';
    setCameraMode('loading');
    let requestedStream: MediaStream | null = null;
    const scannerPromise = getQrmarkScanner();
    try {
      await setPdaInfraredScannerEnabled(false);
      await new Promise<void>((resolve) => {
        window.requestAnimationFrame(() => resolve());
      });
      // Android PDA 释放摄像头比 DOM 隐藏慢。连续关开时必须等上一条 SDK
      // 摄像头真正 close，否则新的 open 可能成功返回但拿不到画面。
      await cameraStopPromiseRef.current.catch(() => undefined);
      if (!openRef.current || session !== sessionRef.current) {
        await setPdaInfraredScannerEnabled(true);
        void scannerPromise.catch(() => undefined);
        return;
      }
      const cameraContainer = cameraContainerRef.current;
      if (!cameraContainer) {
        throw new Error('摄像头预览容器初始化失败');
      }

      const startFallbackCamera = async () => {
        requestedStream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {
            facingMode: { ideal: 'environment' },
            height: { ideal: 1920 },
            width: { ideal: 2560 },
          },
        });
        if (!openRef.current || session !== sessionRef.current) {
          stopStream(requestedStream);
          await setPdaInfraredScannerEnabled(true);
          void scannerPromise.catch(() => undefined);
          return false;
        }
        const fallbackVideo = fallbackVideoRef.current;
        if (!fallbackVideo) {
          throw new Error('备用摄像头预览初始化失败');
        }
        streamRef.current = requestedStream;
        fallbackVideo.srcObject = requestedStream;
        await fallbackVideo.play();
        activeVideoRef.current = fallbackVideo;
        setCameraMode('fallback');
        return true;
      };

      try {
        const startDynamsoftCamera = mode === 'pmi'
          ? startDynamsoftCode128Diagnostic
          : startDynamsoftQrDiagnostic;
        const code128Session = await startDynamsoftCamera(cameraContainer, {
            onAttempt: (attempts) => {
              if (!openRef.current || session !== sessionRef.current) {
                return;
              }
              code128AttemptsRef.current = attempts;
              if (mode === 'pmi' && (attempts === 1 || attempts % 30 === 0)) {
                setStatus(getScanningPrompt('ready', attempts));
                setStatusTone('info');
              }
            },
            onError: (message) => {
              if (!openRef.current || session !== sessionRef.current) {
                return;
              }
              setStatus(mode === 'pmi'
                ? `60×60 条形码识别异常：${message}；80×80 识别继续运行`
                : `普通二维码识别异常：${message}；80×80 识别继续运行`);
              setStatusTone('error');
            },
            onLoading: (message) => {
              if (!openRef.current || session !== sessionRef.current) {
                return;
              }
              setStatus(message);
              setStatusTone('info');
            },
            onReady: () => {
              if (!openRef.current || session !== sessionRef.current) {
                return;
              }
              if (mode === 'pmi') {
                code128StateRef.current = 'ready';
              }
              setStatus(mode === 'pmi'
                ? '摄像头和双识别引擎已就绪，请对准 80×80 或 60×60 标签'
                : '摄像头和二维码识别引擎已就绪，请对准任务码或正式标签');
              setStatusTone('info');
            },
            onResult: (result) => {
              if (openRef.current && session === sessionRef.current) {
                void (mode === 'pmi'
                  ? submitCode128Result(result)
                  : submitPlainQrResult(result));
              }
            },
          });
          if (!openRef.current || session !== sessionRef.current) {
            await code128Session.stop();
            await setPdaInfraredScannerEnabled(true);
            void scannerPromise.catch(() => undefined);
            return;
          }
          code128SessionRef.current = code128Session;
          activeVideoRef.current = code128Session.video;
          if (mode === 'pmi') {
            code128StateRef.current = 'ready';
          }
          code128ErrorRef.current = '';
          setCameraMode('dynamsoft');
      } catch (code128Error) {
        if (!openRef.current || session !== sessionRef.current) {
          await setPdaInfraredScannerEnabled(true);
          void scannerPromise.catch(() => undefined);
          return;
        }
        if (mode === 'pmi') {
          code128StateRef.current = 'failed';
        }
        code128ErrorRef.current = getErrorMessage(code128Error);
        cameraContainer.replaceChildren();
        setStatus(mode === 'pmi'
          ? `60×60 条形码引擎不可用：${code128ErrorRef.current}；正在恢复 80×80 摄像头`
          : `普通二维码引擎不可用：${code128ErrorRef.current}；正在恢复 80×80 摄像头`);
        setStatusTone('error');
        if (!await startFallbackCamera()) {
          return;
        }
      }

      const scanner = await scannerPromise;
      if (!openRef.current || session !== sessionRef.current) {
        await setPdaInfraredScannerEnabled(true);
        return;
      }
      scannerRef.current = scanner;
      scanner.reset();
      if (!activeVideoRef.current) {
        throw new Error('摄像头预览初始化失败');
      }
      setStatus(mode === 'plain'
        ? '请将预送货任务二维码或 80×80 正式标签完整放入取景框'
        : getScanningPrompt(
          code128StateRef.current,
          code128AttemptsRef.current,
          code128ErrorRef.current,
        ));
      setStatusTone('info');
      scheduleScan(0);
    } catch (error) {
      if (!openRef.current || session !== sessionRef.current) {
        await setPdaInfraredScannerEnabled(true);
        return;
      }
      stopStream(requestedStream);
      stopStream(streamRef.current);
      streamRef.current = null;
      await setPdaInfraredScannerEnabled(true);
      void scannerPromise.catch(() => undefined);
      setStatus(getErrorMessage(error));
      setStatusTone('error');
    }
  }, [
    disabled,
    mode,
    onOpenChange,
    scheduleScan,
    submitCode128Result,
    submitPlainQrResult,
  ]);

  useEffect(() => {
    if (disabled && openRef.current) {
      closeCamera();
    }
  }, [closeCamera, disabled]);

  useEffect(() => {
    if (!preloadEngines) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      void getQrmarkScanner().catch(() => undefined);
      void preloadDynamsoftCode128().catch(() => undefined);
    }, 300);

    return () => window.clearTimeout(timer);
  }, [preloadEngines]);

  useEffect(() => {
    if (!open || embedded) {
      return undefined;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeCamera();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [closeCamera, embedded, open]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (openRef.current) {
        onOpenChange?.(false);
      }
      openRef.current = false;
      sessionRef.current += 1;
      cancelScheduledScan();
      const code128Session = code128SessionRef.current;
      code128SessionRef.current = null;
      if (code128Session) {
        cameraStopPromiseRef.current = cameraStopPromiseRef.current
          .catch(() => undefined)
          .then(() => code128Session.stop())
          .catch(() => undefined)
          .then(() => setPdaInfraredScannerEnabled(true))
          .then(() => undefined);
      } else {
        void setPdaInfraredScannerEnabled(true);
      }
      activeVideoRef.current = null;
      stopStream(streamRef.current);
      streamRef.current = null;
      stopFallbackCamera();
      releaseQrmarkScanner();
    };
  }, [cancelScheduledScan, onOpenChange, stopFallbackCamera]);

  return (
    <>
      <button
        className={`qrmark-camera-trigger${open && embedded ? ' qrmark-camera-trigger--active' : ''}`}
        disabled={disabled || closing}
        onClick={() => (open && embedded ? void closeCamera() : void openCamera())}
        type="button"
      >
        {open && embedded ? (
          <X aria-hidden="true" size={20} strokeWidth={2.4} />
        ) : (
          <Camera aria-hidden="true" size={20} strokeWidth={2.4} />
        )}
        {closing ? '正在恢复红外' : open && embedded ? '关闭摄像头' : '摄像头扫码'}
      </button>

      {open && embedded ? (
        <div className="qrmark-camera-inline" data-testid="qrmark-camera-inline">
          <div ref={cameraContainerRef} className="qrmark-camera-sdk-host" />
          <video
            ref={fallbackVideoRef}
            autoPlay
            className={cameraMode === 'fallback' ? 'qrmark-camera-fallback--active' : ''}
            muted
            playsInline
          />
          <div className="qrmark-camera-guide" aria-hidden="true">
            <span />
            <span />
            <span />
            <span />
            <i />
          </div>
        </div>
      ) : null}

      {open && !embedded ? (
        <div className="qrmark-camera-overlay" role="presentation">
          <section
            aria-labelledby="qrmark-camera-title"
            aria-modal="true"
            className="qrmark-camera-dialog"
            role="dialog"
          >
            <header className="qrmark-camera-dialog__head">
              <div>
                <strong id="qrmark-camera-title">{mode === 'plain' ? '贴标核验扫码' : '产品标签识别'}</strong>
                <span>{mode === 'plain' ? '扫描任务二维码或正式标签' : '保持标签完整、画面清晰'}</span>
              </div>
              <button
                aria-label="关闭摄像头扫码"
                disabled={closing}
                onClick={() => void closeCamera()}
                type="button"
              >
                <X aria-hidden="true" size={24} />
              </button>
            </header>

            <div className="qrmark-camera-viewport">
              <div ref={cameraContainerRef} className="qrmark-camera-sdk-host" />
              <video
                ref={fallbackVideoRef}
                autoPlay
                className={cameraMode === 'fallback' ? 'qrmark-camera-fallback--active' : ''}
                muted
                playsInline
              />
              <div className="qrmark-camera-guide" aria-hidden="true">
                <span />
                <span />
                <span />
                <span />
                <i />
              </div>
            </div>

            <div
              aria-live="polite"
              className={`qrmark-camera-status qrmark-camera-status--${statusTone}`}
              role="status"
            >
              <ScanLine aria-hidden="true" size={18} />
              <span>{status}</span>
            </div>

            <div className="qrmark-camera-actions">
              <button disabled={closing} onClick={() => void closeCamera()} type="button">
                {closing ? '正在恢复红外…' : '关闭'}
              </button>
            </div>
          </section>
        </div>
      ) : null}
    </>
  );
}

export default QrmarkCameraScanner;
